import type { AccountBreakdown, CardBreakdown } from '../../../hooks/useReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface Props {
  accounts: AccountBreakdown[];
  cards: CardBreakdown[];
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const COLORS = ['#10B981', '#F59E0B', '#6366F1', '#EC4899', '#06B6D4', '#F97316'];

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-[12px]">
      <p className="font-600 text-gray-700">{payload[0].payload.name}</p>
      <p className="text-gray-600 mt-0.5">Gastos: {fmt(payload[0].value)}</p>
      {payload[0].payload.pct !== undefined && <p className="text-gray-500">{payload[0].payload.pct}% do total</p>}
    </div>
  );
};

export const AccountCardBreakdown = ({ accounts, cards, loading }: Props) => {
  if (loading) return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
  );

  const accountChartData = accounts.map((a, i) => ({ name: a.name, value: a.totalExpenses, pct: a.pct, color: COLORS[i % COLORS.length] }));
  const cardChartData = cards.map((c, i) => ({ name: c.name, value: c.totalExpenses, pct: c.pct, color: COLORS[i % COLORS.length] }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Accounts */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5">
        <div className="mb-4">
          <h3 className="text-[14px] font-700 text-gray-900">Gastos por Conta</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">Despesas distribuídas entre contas bancárias</p>
        </div>

        {accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-[12px] gap-2">
            <i className="ri-bank-line text-2xl text-gray-200" />
            Sem movimentações por conta no período
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={accountChartData} layout="vertical" barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => `R$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {accountChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div className="space-y-3 mt-4">
              {accounts.map((acc, i) => (
                <div key={acc.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-[12px] font-500 text-gray-700">{acc.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-right">
                      <span className="text-[11px] text-gray-400">{acc.pct}%</span>
                      <span className="text-[12px] font-700 text-gray-800">{fmt(acc.totalExpenses)}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${acc.pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                  </div>
                  <div className="flex justify-between mt-0.5 text-[10px] text-gray-400">
                    <span>Saldo atual: {fmt(acc.currentBalance)}</span>
                    <span>Receitas: {fmt(acc.totalIncome)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Cards */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5">
        <div className="mb-4">
          <h3 className="text-[14px] font-700 text-gray-900">Gastos por Cartão</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">Despesas distribuídas entre cartões de crédito</p>
        </div>

        {cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-[12px] gap-2">
            <i className="ri-bank-card-line text-2xl text-gray-200" />
            Sem movimentações por cartão no período
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={cardChartData} layout="vertical" barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => `R$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {cardChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div className="space-y-3 mt-4">
              {cards.map((card, i) => (
                <div key={card.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-[12px] font-500 text-gray-700">{card.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-right">
                      <span className="text-[11px] text-gray-400">{card.pct}%</span>
                      <span className="text-[12px] font-700 text-gray-800">{fmt(card.totalExpenses)}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${card.pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                  </div>
                  {card.pendingAmount > 0 && (
                    <p className="text-[10px] text-amber-500 mt-0.5">Pendente: {fmt(card.pendingAmount)}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
